import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useConsumer } from '../../contexts/ConsumerContext';
import { getParcelInfo } from '../../services';
import { MOBIUSPACK_DEFAULT_BRAND, DEFAULT_INFO } from '../mocks/consumerBrands';

export function buildBrandFromApiData(data) {
  const clientColorGradient = data?.clientColorGradient || null;
  return {
    ...MOBIUSPACK_DEFAULT_BRAND,
    ...DEFAULT_INFO,
    brandName: data?.clientName || MOBIUSPACK_DEFAULT_BRAND.brandName,
    brandLogoUrl: data?.clientLogoUrl || MOBIUSPACK_DEFAULT_BRAND.brandLogoUrl,
    brandColor: data?.clientColor || MOBIUSPACK_DEFAULT_BRAND.brandColor,
    brandTextColor: data?.clientTextColor || MOBIUSPACK_DEFAULT_BRAND.brandTextColor,
    clientColorGradient,
    brandGradient: clientColorGradient?.length >= 2
      ? `linear-gradient(150deg, ${clientColorGradient[0]} 0%, ${clientColorGradient[1]} 100%)`
      : MOBIUSPACK_DEFAULT_BRAND.brandGradient,
  };
}

export function buildParcelFromApiData(data, token) {
  const carrierBarCodeRaw = data?.partnerID || null;
  return {
    id: data?.id || null,
    token: token || null,
    type: data?.type || null,
    version: data?.version || null,
    barCode: carrierBarCodeRaw ? String(carrierBarCodeRaw).replace(/\D/g, '') : null,
    shippingNumber: data?.shippingNumber || null,
    deliveryContext: data?.deliveryContext || null,
  };
}

export function useParcelLoader() {
  const { parcelId } = useParams();
  const { parcel, brand, dispatch } = useConsumer();
  const [loading, setLoading] = useState(!parcel && !!parcelId);
  const [notFound, setNotFound] = useState(false);
  const isFetchingRef = useRef(false);

  const fetchParcel = useCallback(async (id) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    setNotFound(false);
    try {
      const response = await getParcelInfo(id);
      if (response.status !== 200) {
        setNotFound(true);
        throw new Error(`Code: ${response.status}`);
      }
      const data = response.results;
      dispatch({ type: 'CONSUMER_BRAND_LOADED', brand: buildBrandFromApiData(data) });
      dispatch({ type: 'CONSUMER_PARCEL_LOADED', parcel: buildParcelFromApiData(data, id) });
    } catch (error) {
      console.error('useParcelLoader: fetch failed', error);
      setNotFound(true);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!parcelId) return;
    const needsFetch = !parcel || parcel.token !== parcelId;
    if (needsFetch) {
      if (parcel) dispatch({ type: 'CONSUMER_RESET_KEEP_BRAND' });
      fetchParcel(parcelId);
    }
  }, [parcel, parcelId, fetchParcel, dispatch]);

  return { parcel, brand, loading: loading || (!parcel && !notFound && !!parcelId), notFound };
}
